import type { AssetRecord, ContentBundle, ContentMeta, ContentSummary, CurriculumCategory, Question, SourceRecord, Track } from './types';
import { publicationMappings } from '../../shared/publication';

// Vite serves the generated preview in development. Both deployable builds use assets in release/;
// meta.mode still distinguishes the protected preview from the approved public bank.
export const contentBase = import.meta.env.DEV && __CONTENT_MODE__ === 'preview' ? '/content/preview' : '/content/release';

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(contentBase + '/' + path, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Nie udało się wczytać treści (' + response.status + ').');
  return response.json() as Promise<T>;
}

let pendingContent: Promise<ContentBundle> | undefined;
let pendingSummary: Promise<ContentSummary> | undefined;

export function initialContentSummary(): ContentSummary | null {
  const value = typeof document === 'undefined' ? null : document.getElementById('content-summary')?.textContent;
  if (!value) return null;
  try { return JSON.parse(value) as ContentSummary; } catch { return null; }
}

export function loadContentSummary(): Promise<ContentSummary> {
  return pendingSummary ??= getJson<ContentSummary>('summary.json').catch(error => { pendingSummary = undefined; throw error; });
}

// Share the in-flight request across StrictMode mounts and route changes; allow retry after failure.
export function loadContent(): Promise<ContentBundle> {
  return pendingContent ??= fetchContent().catch(error => { pendingContent = undefined; throw error; });
}

async function fetchContent(): Promise<ContentBundle> {
  const [questions, assets, sources, curriculum, meta] = await Promise.all([
    getJson<{ questions: Question[] }>('questions.json'),
    getJson<{ assets: AssetRecord[] }>('assets.json'),
    getJson<{ sources: SourceRecord[] }>('sources.json'),
    getJson<{ categories: CurriculumCategory[] }>('curriculum.json'),
    getJson<ContentMeta>('meta.json')
  ]);
  const expired = new Set(publicationMappings.filter(row => row.licenseEndsAt && new Date(row.licenseEndsAt).getTime() <= Date.now()).map(row => row.questionId));
  if (publicationMappings.length) {
    const response = await fetch('/api/v1/content/rights', { cache: 'no-store' });
    if (!response.ok) throw new Error('Nie udało się sprawdzić dostępności materiałów. Spróbuj ponownie.');
    const rights = await response.json() as { blocked: string[] };
    rights.blocked.forEach(id => expired.add(id));
  }
  return {
    questions: questions.questions.filter(question => !expired.has(question.id)),
    assets: new Map(assets.assets.map((asset) => [asset.id, asset])),
    sources: new Map(sources.sources.map((source) => [source.id, source])),
    categories: curriculum.categories,
    meta,
    contentBase
  };
}

export function questionsForTrack(questions: Question[], track: Track): Question[] {
  return questions.filter((question) => question.primary_objectives.some((objective) => objective.track === track));
}

export function attachmentUrl(bundle: ContentBundle, assetId: string): string | undefined {
  const asset = bundle.assets.get(assetId);
  return asset ? bundle.contentBase + '/assets/' + asset.path.split('/').pop() : undefined;
}
